/**
 * opSource — data-source transparency convention (2026-07-16 operator decision).
 *
 * Any layer serving a DECLARED-but-not-implemented op (CD preview seeds, temp
 * stubs) marks its JSON with `__source: "cd-preview-seed"`. Real backend /
 * real api-mocks responses never carry the marker. Call sites report every op
 * result here (see wf-api.ts `tag`); screens render <DataSourceStrip> naming
 * exactly which of their calls are not clean — a feature-by-feature to-do
 * signal for the backend team (REQ-034). When an op ships for real, its chip
 * disappears with zero UI changes.
 */
import { useEffect, useState } from "react";
import { Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

export const SEED_MARK = "cd-preview-seed";

const sourceByOp = new Map<string, boolean>(); // op -> seeded?
const subs = new Set<() => void>();

export function reportOpSource(op: string, body: unknown): void {
  const isSeed = !!body && typeof body === "object" && (body as Record<string, unknown>).__source === SEED_MARK;
  if (sourceByOp.get(op) === isSeed) return;
  sourceByOp.set(op, isSeed);
  subs.forEach((f) => f());
}

export function seededOps(filter?: string[]): string[] {
  const all = [...sourceByOp.entries()].filter(([, s]) => s).map(([op]) => op);
  const list = filter ? all.filter((op) => filter.includes(op)) : all;
  return list.sort();
}

export function useSeededOps(filter?: string[]): string[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return seededOps(filter);
}

/**
 * The convention's visual: a dashed amber strip listing this screen's
 * non-clean ops. Renders nothing when every consumed op is served by the
 * integrated backend (or hasn't been called yet).
 */
export function DataSourceStrip({ screen, ops, reqId = "REQ-034" }: { screen: string; ops: string[]; reqId?: string }) {
  const t = useT();
  const seeded = useSeededOps(ops);
  if (seeded.length === 0) return null;
  // Operator direction (2026-07-18): keep reportOpSource/useSeededOps tracking
  // wired (so it's ready the moment a real backend lands) but don't surface
  // the visual strip. Flip this back to `false` to re-show it.
  const HIDDEN = true;
  if (HIDDEN) return null;
  return (
    <div data-testid={`${screen}-seed-strip`} role="note"
      style={{ margin: "12px 24px 0", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "7px 12px", borderRadius: 8, border: `1px dashed ${alpha(T.orange1, 55)}`, background: alpha(T.orange1, 7), fontSize: 12, color: T.n2, lineHeight: 1.4 }}>
      <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0 }}><Icon.warn size={13} /></span>
      <b style={{ color: T.n1, fontWeight: 700 }}>{t("shell.opSource.previewSeedData", "Preview seed data")}</b>
      <span>{t("shell.opSource.notYetServedByTheIntegratedBackend", "— not yet served by the integrated backend:")}</span>
      {seeded.map((op) => (
        <code key={op} style={{ fontFamily: T.fontMono, fontSize: 10.5, fontWeight: 600, color: T.orange1, background: alpha(T.orange1, 12), borderRadius: 4, padding: "1px 6px" }}>{op}</code>
      ))}
      <span style={{ marginLeft: "auto", color: T.soft, whiteSpace: "nowrap" }}>{t("shell.opSource.backendToDo", "Backend to-do:")}{" "}{reqId}</span>
    </div>
  );
}
